import React, { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Download, Upload, Loader2, CheckCircle2, AlertCircle, FileSpreadsheet } from "lucide-react";

export default function AdminPricelist() {
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const onDownload = async () => {
    setDownloading(true); setError("");
    try {
      const res = await api.get("/admin/pricelist/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "MAK_Preisliste_Vorlage.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setDownloading(false); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setError(""); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/pricelist/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <div className="p-6 md:p-10 max-w-4xl" data-testid="admin-pricelist-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Administration</div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Preisliste verwalten</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Laden Sie die Excel-Vorlage herunter, aktualisieren Sie die Preise und laden Sie die Datei wieder hoch.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-zinc-200">
        {/* Download */}
        <div className="p-6 md:p-8 border-b md:border-b-0 md:border-r border-zinc-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-zinc-900 text-white flex items-center justify-center">
              <Download size={16} />
            </div>
            <h3 className="font-semibold text-zinc-900">1. Vorlage herunterladen</h3>
          </div>
          <p className="text-sm text-zinc-600 mb-5 leading-relaxed">
            Die Vorlage enthält alle aktuellen Preise. Tragen Sie neue Preise in den gelben Feldern ein.
            Sie können auch neue Zeilen oder Spalten hinzufügen — der Importer erkennt diese automatisch.
          </p>
          <button
            onClick={onDownload}
            disabled={downloading}
            data-testid="download-template-button"
            className="bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-medium px-4 py-2.5 rounded-sm flex items-center gap-2 disabled:opacity-60"
          >
            {downloading ? <Loader2 size={14} className="animate-spin"/> : <FileSpreadsheet size={14}/>}
            Vorlage herunterladen
          </button>
        </div>

        {/* Upload */}
        <div className="p-6 md:p-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-zinc-900 text-white flex items-center justify-center">
              <Upload size={16} />
            </div>
            <h3 className="font-semibold text-zinc-900">2. Aktualisierte Vorlage hochladen</h3>
          </div>
          <p className="text-sm text-zinc-600 mb-5 leading-relaxed">
            Nach dem Hochladen werden alle Preise sofort aktualisiert. Die alte Preisliste wird automatisch
            als Backup gesichert.
          </p>
          <label className="inline-flex items-center gap-2 bg-white hover:bg-zinc-50 border border-zinc-300 text-zinc-900 text-sm font-medium px-4 py-2.5 rounded-sm cursor-pointer">
            {uploading ? <Loader2 size={14} className="animate-spin"/> : <Upload size={14}/>}
            <span>Datei wählen</span>
            <input type="file" accept=".xlsx,.xls" onChange={onUpload} disabled={uploading}
                   data-testid="upload-template-input" className="hidden"/>
          </label>
        </div>
      </div>

      {/* Result */}
      {result && (
        <div className="mt-6 border border-emerald-200 bg-emerald-50 p-4 rounded-sm" data-testid="import-success">
          <div className="flex items-start gap-3">
            <CheckCircle2 size={18} className="text-emerald-600 mt-0.5"/>
            <div className="text-sm">
              <div className="font-medium text-emerald-900">Preisliste aktualisiert!</div>
              <div className="text-emerald-700 mt-1">
                {result.productCount} Produkte, {result.accessoryCount} Zubehör-Kategorien importiert.
              </div>
              <div className="text-xs text-emerald-600 mt-1">
                Backup gespeichert: <span className="font-mono">{result.backup}</span>
              </div>
            </div>
          </div>
        </div>
      )}
      {error && (
        <div className="mt-6 border border-red-200 bg-red-50 p-4 rounded-sm">
          <div className="flex items-start gap-3">
            <AlertCircle size={18} className="text-red-600 mt-0.5"/>
            <div className="text-sm text-red-900">{error}</div>
          </div>
        </div>
      )}

      {/* Anleitung */}
      <div className="mt-10 border border-zinc-200 p-6 bg-zinc-50">
        <h4 className="text-sm font-semibold text-zinc-900 mb-3 uppercase tracking-widest">Hinweise zur Vorlage</h4>
        <ul className="text-sm text-zinc-600 space-y-2 list-disc pl-5">
          <li>Jedes Modell befindet sich auf einem eigenen Blatt (ASTRAL, ECLIPSE, LUNAR, ...).</li>
          <li>Pro Blatt gibt es 4 Sektionen: ZAUNELEMENT, GEHTÜRE, DOPPELFLÜGELTOR, FREITRAGENDER SCHIEBETOR.</li>
          <li>PFOSTEN: Höhe in der ersten Spalte, Profiltyp in der Kopfzeile.</li>
          <li>Leere Felder werden als „nicht verfügbar" interpretiert.</li>
          <li>Sie können neue Zeilen für zusätzliche Höhen oder neue Spalten für neue Profile hinzufügen.</li>
        </ul>
      </div>
    </div>
  );
}
